import React, { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Camera, ArrowSquareOut, X, Check } from "@phosphor-icons/react";

/**
 * Inline profile editor card for the Profile page.
 *
 * Lets the user upload an avatar (≤350 KB after the resize), pick their home
 * course from our 5 Fiji courses, set a declared starting handicap (used
 * until they have 3+ recorded rounds), and link their Golf Australia /
 * GolfLink number with an outbound view link to golf.com.au.
 */
export default function EditProfileSection() {
  const { user, refreshUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [courses, setCourses] = useState([]);
  const [name, setName] = useState(user?.name || "");
  const [homeCourse, setHomeCourse] = useState(user?.home_course_id || "");
  const [declared, setDeclared] = useState(user?.declared_handicap ?? "");
  const [golflink, setGolflink] = useState(user?.golflink_number || "");
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/courses").then((r) => setCourses(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setName(user?.name || "");
    setHomeCourse(user?.home_course_id || "");
    setDeclared(user?.declared_handicap ?? "");
    setGolflink(user?.golflink_number || "");
  }, [user]);

  const onPickFile = () => fileRef.current?.click();

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp)$/i.test(file.type)) {
      toast.error("PNG, JPEG or WebP only"); return;
    }
    // Downsize to a 256×256 PNG via canvas to stay under the 350 KB cap.
    const dataUrl = await fileToResizedDataUrl(file, 256);
    setSaving(true);
    try {
      await api.put("/users/me/avatar", { data_url: dataUrl });
      await refreshUser();
      toast.success("Avatar updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally { setSaving(false); }
    e.target.value = "";  // allow re-select of same file
  };

  const removeAvatar = async () => {
    setSaving(true);
    try {
      await api.delete("/users/me/avatar");
      await refreshUser();
      toast.success("Avatar removed");
    } catch (err) {
      toast.error("Couldn't remove");
    } finally { setSaving(false); }
  };

  const save = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      const payload = { name };
      payload.home_course_id = homeCourse || "";
      payload.declared_handicap = declared === "" ? null : parseFloat(declared);
      payload.golflink_number = golflink.trim();
      await api.put("/users/me", payload);
      await refreshUser();
      toast.success("Profile saved");
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="card-default p-7" data-testid="edit-profile-section">
      <div className="flex items-start gap-5 flex-wrap">
        <div className="relative">
          {user?.avatar_b64 ? (
            <img src={user.avatar_b64} alt="" className="w-20 h-20 rounded-2xl object-cover" data-testid="profile-avatar-img" />
          ) : (
            <div className="w-20 h-20 rounded-2xl bg-[#0F2537] text-white flex items-center justify-center font-heading font-black text-3xl">
              {(user?.name || "?").slice(0, 1).toUpperCase()}
            </div>
          )}
          <button onClick={onPickFile} disabled={saving} data-testid="upload-avatar-btn"
            className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-[#E05D3A] text-white shadow flex items-center justify-center hover:brightness-110 disabled:opacity-60">
            <Camera weight="fill" size={14} />
          </button>
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={onFileChange} data-testid="avatar-file-input" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-bold text-2xl truncate">{user?.name}</div>
          <div className="text-sm text-[#0F2537]/70 truncate">{user?.email}</div>
          <div className="mt-1 text-xs text-[#0F2537]/60 flex flex-wrap gap-x-3 gap-y-1">
            <span>Member since {user?.created_at ? new Date(user.created_at).toLocaleDateString() : "—"}</span>
            {user?.golflink_number && (
              <a href={`https://www.golf.com.au/`} target="_blank" rel="noopener noreferrer" data-testid="golflink-external-link"
                className="inline-flex items-center gap-1 text-[#E05D3A] font-semibold hover:underline">
                <ArrowSquareOut size={12} weight="bold" /> GolfLink {user.golflink_number}
              </a>
            )}
            {user?.avatar_b64 && (
              <button onClick={removeAvatar} data-testid="remove-avatar-btn" className="text-[#0F2537]/50 hover:text-red-600">Remove avatar</button>
            )}
          </div>
        </div>
        <button onClick={() => setOpen((v) => !v)} data-testid="toggle-edit-profile-btn"
          className="px-4 py-2.5 rounded-xl border border-[#E2E0D8] text-[#0F2537] font-bold text-sm">
          {open ? "Cancel" : "Edit"}
        </button>
      </div>

      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        <div className="rounded-xl bg-[#F8F7F3] p-5">
          <div className="label-eyebrow">Handicap Index</div>
          <div className="font-heading font-black text-3xl mt-1">{user?.handicap_index ?? "—"}</div>
          {user?.handicap_source && (
            <div className="mt-1 text-[10px] uppercase tracking-widest text-[#0F2537]/55 font-bold">
              {user.handicap_source === "whs" ? "Calculated · WHS" : "Declared · pending rounds"}
            </div>
          )}
        </div>
        <div className="rounded-xl bg-[#F8F7F3] p-5">
          <div className="label-eyebrow">Home course</div>
          <div className="font-heading font-bold text-lg mt-1 truncate">{user?.home_club || "Not set"}</div>
        </div>
      </div>

      {open && (
        <form onSubmit={save} className="mt-6 pt-6 border-t border-[#E2E0D8] space-y-4" data-testid="edit-profile-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-[#0F2537]/70">Name</label>
              <input value={name} onChange={(e) => setName(e.target.value)} data-testid="edit-name-input" maxLength={80}
                className="mt-1 w-full px-4 py-2.5 rounded-xl border border-[#E2E0D8] bg-white focus:outline-none focus:border-[#0F2537]" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#0F2537]/70">Home course</label>
              <select value={homeCourse} onChange={(e) => setHomeCourse(e.target.value)} data-testid="edit-home-course-select"
                className="mt-1 w-full px-4 py-2.5 rounded-xl border border-[#E2E0D8] bg-white focus:outline-none focus:border-[#0F2537]">
                <option value="">— Not set —</option>
                {courses.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#0F2537]/70">Declared starting handicap</label>
              <input type="number" step="0.1" min="-10" max="54" value={declared} onChange={(e) => setDeclared(e.target.value)}
                data-testid="edit-declared-handicap-input" placeholder="e.g. 14.5"
                className="mt-1 w-full px-4 py-2.5 rounded-xl border border-[#E2E0D8] bg-white focus:outline-none focus:border-[#0F2537]" />
              <p className="text-[10px] text-[#0F2537]/50 mt-1">Used as your index until you record 3 rounds — then WHS takes over.</p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#0F2537]/70">GolfLink / GA number</label>
              <input value={golflink} onChange={(e) => setGolflink(e.target.value)} data-testid="edit-golflink-input"
                placeholder="6-10 digit GA Connect number"
                className="mt-1 w-full px-4 py-2.5 rounded-xl border border-[#E2E0D8] bg-white focus:outline-none focus:border-[#0F2537]" />
              <p className="text-[10px] text-[#0F2537]/50 mt-1">For Australian / Fiji golfers using <a href="https://golf.com.au" target="_blank" rel="noopener noreferrer" className="text-[#E05D3A] font-semibold">golf.com.au</a>. We don't verify it automatically.</p>
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => setOpen(false)} data-testid="edit-cancel-btn"
              className="px-4 py-2.5 rounded-xl border border-[#E2E0D8] text-[#0F2537] font-bold text-sm inline-flex items-center gap-1">
              <X size={14} weight="bold" /> Cancel
            </button>
            <button type="submit" disabled={saving} data-testid="edit-save-profile-btn"
              className="px-4 py-2.5 rounded-xl bg-[#0F2537] text-white font-bold text-sm inline-flex items-center gap-1 disabled:opacity-60">
              <Check size={14} weight="bold" /> {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

// --- Helpers ---------------------------------------------------------------
function fileToResizedDataUrl(file, max = 256) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("Bad image"));
      img.onload = () => {
        const scale = Math.min(max / img.width, max / img.height, 1);
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";
        ctx.drawImage(img, 0, 0, w, h);
        // Prefer JPEG for photos (smaller). Fallback PNG if alpha.
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}
